/**
 * Utility functions for note and frequency calculations
 */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Calculate frequency for a given MIDI note number
 * @param midiNote MIDI note number (A4 = 69)
 * @param referenceFreq Reference frequency for A4 (default: 440 Hz)
 * @returns Frequency in Hz
 */
export function midiToFrequency(midiNote: number, referenceFreq = 440): number {
  return referenceFreq * Math.pow(2, (midiNote - 69) / 12);
}

/**
 * Calculate MIDI note number for a given frequency
 * @param frequency Frequency in Hz
 * @param referenceFreq Reference frequency for A4 (default: 440 Hz)
 * @returns MIDI note number
 */
export function frequencyToMidi(frequency: number, referenceFreq = 440): number {
  return 69 + 12 * Math.log2(frequency / referenceFreq);
}

/**
 * Get note name from MIDI note number
 * @param midiNote MIDI note number
 * @returns Note name with octave (e.g., 'A4', 'C#3')
 */
export function midiToNoteName(midiNote: number): string {
  const roundedMidi = Math.round(midiNote);
  const noteIndex = roundedMidi % 12;
  const octave = Math.floor(roundedMidi / 12) - 1;
  return `${NOTE_NAMES[noteIndex]}${octave}`;
}

/**
 * Get note name from frequency
 * @param frequency Frequency in Hz
 * @param referenceFreq Reference frequency for A4 (default: 440 Hz)
 * @returns Note name with octave (e.g., 'A4', 'C#3')
 */
export function frequencyToNoteName(frequency: number, referenceFreq = 440): string {
  const midiNote = frequencyToMidi(frequency, referenceFreq);
  return midiToNoteName(midiNote);
}

/**
 * Calculate cents difference between two frequencies
 * Cents are a logarithmic unit of measure: 100 cents = 1 semitone
 * @param frequency Detected frequency in Hz
 * @param targetFrequency Target frequency in Hz
 * @returns Cents difference (positive = sharp, negative = flat)
 */
export function calculateCents(frequency: number, targetFrequency: number): number {
  return 1200 * Math.log2(frequency / targetFrequency);
}

/**
 * Find the closest frequency from a list of target frequencies
 * @param frequency Detected frequency in Hz
 * @param targetFrequencies Array of target frequencies
 * @returns Object with closest frequency, its index, and cents offset
 */
export function findClosestFrequency(
  frequency: number,
  targetFrequencies: number[]
): {
  frequency: number;
  index: number;
  cents: number;
} {
  let closestIndex = 0;
  let minCentsDiff = Infinity;

  for (let i = 0; i < targetFrequencies.length; i++) {
    const cents = Math.abs(calculateCents(frequency, targetFrequencies[i]));
    if (cents < minCentsDiff) {
      minCentsDiff = cents;
      closestIndex = i;
    }
  }

  const closestFreq = targetFrequencies[closestIndex];
  return {
    frequency: closestFreq,
    index: closestIndex,
    cents: calculateCents(frequency, closestFreq),
  };
}

/**
 * Check if a frequency is within tolerance of a target frequency
 * @param frequency Detected frequency in Hz
 * @param targetFrequency Target frequency in Hz
 * @param toleranceCents Tolerance in cents (default: 5)
 * @returns True if within tolerance
 */
export function isInTune(
  frequency: number,
  targetFrequency: number,
  toleranceCents = 5
): boolean {
  const cents = Math.abs(calculateCents(frequency, targetFrequency));
  return cents <= toleranceCents;
}
